import { createContext, useReducer, useContext, ReactNode, useCallback } from 'react';
import { subDays } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { searchReducer, getInitialState, getEmptyFilterState } from './SearchReducer';
import {
  SearchContextType,
  ApplyFilterPayload,
  FilterState,
} from './types';

const SearchContext = createContext<SearchContextType | undefined>(undefined);

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider = ({ children }: SearchProviderProps) => {
  const [searchParams] = useSearchParams();
  const sortingParam = searchParams.get('sorting') ?? '-published_at';

  const [state, dispatch] = useReducer(
    searchReducer,
    getInitialState(searchParams, sortingParam)
  );

  // Helper functions that wrap dispatch calls
  const applyFilter = useCallback((payload: ApplyFilterPayload) => {
    dispatch({ type: 'APPLY_FILTER', payload });
  }, []);

  const updateLanguages = useCallback((languages: number[]) => {
    dispatch({ type: 'UPDATE_LANGUAGES', payload: languages });
  }, []);

  const setDate = useCallback((startDate: Date | null, endDate: Date | null) => {
    dispatch({ type: 'SET_DATE', payload: { startDate, endDate } });
  }, []);

  const setSimilarTo = useCallback((
    examples: string[] | null,
    startDate: Date | null,
    endDate: Date | null
  ) => {
    dispatch({ type: 'SET_SIMILAR_TO', payload: { examples, startDate, endDate } });
  }, []);

  const setCommentsTo = useCallback((parentCompositeId: string | null) => {
    dispatch({ type: 'SET_COMMENTS_TO', payload: { parentCompositeId } });
  }, []);

  const setRepostsTo = useCallback((parentCompositeId: string | null) => {
    dispatch({ type: 'SET_REPOSTS_TO', payload: { parentCompositeId } });
  }, []);

  const setExamples = useCallback((examples: string[] | null) => {
    dispatch({ type: 'SET_EXAMPLES', payload: { examples } });
  }, []);

  const sortBy = useCallback((fieldName: string) => {
    dispatch({ type: 'SORT_BY', fieldName });
  }, []);

  const setIsAscending = useCallback((isAscending: boolean) => {
    dispatch({ type: 'IS_ASCENDING', isAscending });
  }, []);

  const setCursor = useCallback((cursor: string | null) => {
    dispatch({ type: 'SET_CURSOR', cursor });
  }, []);

  const resetFilters = useCallback(() => {
    const emptyFilters = getEmptyFilterState();
    dispatch({
      type: 'APPLY_FILTER',
      payload: emptyFilters,
    });
    dispatch({
      type: 'SET_DATE',
      payload: { startDate: subDays(new Date(), 7), endDate: new Date() },
    });
    dispatch({ type: 'SORT_BY', fieldName: 'DATE' });
    dispatch({ type: 'IS_ASCENDING', isAscending: false });
  }, []);

  // Draft filter functions
  const updateDraftFilters = useCallback((filters: Partial<FilterState>) => {
    dispatch({ type: 'UPDATE_DRAFT_FILTERS', payload: filters });
  }, []);

  const applyDraftFilters = useCallback(() => {
    dispatch({ type: 'APPLY_DRAFT_FILTERS' });
  }, []);

  const resetDraftFilters = useCallback(() => {
    dispatch({ type: 'RESET_DRAFT_FILTERS' });
  }, []);

  const syncFiltersToDraft = useCallback(() => {
    dispatch({ type: 'SYNC_FILTERS_TO_DRAFT' });
  }, []);

  const value: SearchContextType = {
    state,
    dispatch,
    applyFilter,
    updateLanguages,
    setDate,
    setSimilarTo,
    setCommentsTo,
    setRepostsTo,
    setExamples,
    sortBy,
    setIsAscending,
    setCursor,
    resetFilters,
    updateDraftFilters,
    applyDraftFilters,
    resetDraftFilters,
    syncFiltersToDraft,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

// Custom hook to use the SearchContext
export const useSearchContext = (): SearchContextType => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return context;
};